import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useReconnectingSocket } from '@/shared/hooks/useReconnectingSocket';
import { getWsOrigin, env } from '@/shared/lib/env';
import { getAccessToken } from '@/shared/lib/auth';
import { queryKeys } from '@/shared/api/queryKeys';

/** Frame pushed by AdminInboxConsumer.chat_inbox. */
type ChatInboxEvent = {
  type: 'inbox';
  conversation_id: string;
  is_user_message: boolean;
};

const isChatInboxEvent = (data: unknown): data is ChatInboxEvent =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === 'inbox';

/**
 * Opens `wss://<api>/ws/admin/inbox?token=<jwt>` for the lifetime of the app
 * shell. On every `chat.inbox` event it invalidates the unread-count and
 * conversation-list queries so the sidebar badge + list update live, replacing
 * the old 12-15s polling. A single refetch on (re)connect resyncs state.
 *
 * Mounted unconditionally; stays closed (url=null) until a token exists.
 */
export const useAdminInboxSocket = (): void => {
  const qc = useQueryClient();
  const token = getAccessToken();

  const url = useMemo(() => {
    const origin = getWsOrigin(env.apiUrl);
    if (!token || !origin) return null;
    return `${origin}/ws/admin/inbox?token=${encodeURIComponent(token)}`;
  }, [token]);

  const refreshInbox = useCallback(() => {
    void qc.invalidateQueries({ queryKey: queryKeys.supportChat.unreadCount });
    void qc.invalidateQueries({
      queryKey: ['support-chat', 'conversations'],
    });
  }, [qc]);

  const onMessage = useCallback(
    (data: unknown) => {
      if (!isChatInboxEvent(data)) return;
      refreshInbox();
    },
    [refreshInbox],
  );

  useReconnectingSocket({ url, onMessage, onOpen: refreshInbox });
};
